import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";
import ClusterMap from "@/components/ClusterMap";
import LatestInsights from "@/components/LatestInsights";
import LogoReveal from "@/components/LogoReveal";
import { cases } from "@/data/cases";
import { useSeo } from "@/lib/seo";
import { useStartProject } from "@/lib/startProjectContext";
import { ShieldCheck, ArrowRight } from "lucide-react";
import {
  SERVICES_LD,
  TESTING_LD,
  TESTING_CATEGORIES,
  FAQ_LD,
} from "@/lib/structuredData";

const HERO_BG = "https://images.pexels.com/photos/12951630/pexels-photo-12951630.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2000";

const HERO_META = [
  { k: "Established", v: "2002 · Moradabad" },
  { k: "Vetted factories", v: "1,000+" },
  { k: "Clusters", v: "15 · India-wide" },
  { k: "US shipment share", v: "40% of volume" },
];

const HERO_WORDS = ["India", "Sourcing", "Agent", "for", "Lighting,", "Furniture", "&", "Home", "Décor"];

const CAPABILITIES = [
  {
    id: "furniture",
    label: "Furniture",
    to: "/categories/furniture",
    ix: "01",
    image: "https://images.unsplash.com/photo-1682718619781-252f23e21132?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwZnVybml0dXJlJTIwbWFudWZhY3R1cmluZyUyMGZhY3Rvcnl8ZW58MHx8fHwxNzg3MzU1MzA1fDA&ixlib=rb-4.1.0&q=85",
    tagline: "Jodhpur · Saharanpur",
    body: "Solid wood, iron, upholstered case goods. Kiln-dried timber, workshop-grade joinery, factory-of-record accountability from PO to container seal.",
  },
  {
    id: "lighting",
    label: "Lighting",
    to: "/categories/lighting",
    ix: "02",
    image: "https://images.pexels.com/photos/30912899/pexels-photo-30912899.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
    tagline: "Moradabad · Firozabad",
    body: "Brass, ceramic and hand-blown glass pendants. UL / CE / SAA certified drivers, photometric QC, retail-grade finish quality.",
  },
  {
    id: "home-decor",
    label: "Home décor",
    to: "/categories/home-decor",
    ix: "03",
    image: "https://images.pexels.com/photos/14605487/pexels-photo-14605487.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
    tagline: "Jaipur · Agra · Noida",
    body: "Blue pottery, marble inlay, brass décor and mixed-media accessories. Small-batch craft economics with retail-shelf reliability.",
  },
];

/** Premium hero — charcoal + staggered reveal + single CTA. */
const QuietLuxuryHero = () => {
  const { open } = useStartProject();
  return (
    <section className="ql-hero" data-testid="ql-hero">
      <div
        className="ql-hero-bg"
        style={{ backgroundImage: `url("${HERO_BG}")` }}
        aria-hidden="true"
      />
      <div className="ql-hero-veil" aria-hidden="true" />
      <div className="ql-hero-inner">
        <p className="ql-hero-eyebrow" data-testid="ql-hero-eyebrow">
          Est. 2002 · Moradabad · India sourcing house
        </p>
        <h1 className="ql-hero-h1" data-testid="ql-hero-h1" aria-label="India Sourcing Agent for Lighting, Furniture & Home Décor">
          {HERO_WORDS.map((w, i) => (
            <span key={`${w}-${i}`} className="word" style={{ animationDelay: `${180 + i * 90}ms` }} aria-hidden="true">
              {w}
              {i < HERO_WORDS.length - 1 ? "\u00A0" : ""}
            </span>
          ))}
        </h1>
        <p className="ql-hero-lede" data-testid="ql-hero-lede">
          A buyer-side India sourcing partnership for retailers, hospitality and DTC brands. Twenty-three years on the factory floor across fifteen manufacturing clusters — lighting, furniture, home décor, brass, textile. One accountable team from brief to container seal.
        </p>
        <div className="ql-hero-actions">
          <button
            type="button"
            className="ql-cta-primary"
            onClick={open}
            data-testid="hero-start-project"
          >
            Start Your Project
            <ArrowRight size={16} strokeWidth={1.6} aria-hidden />
          </button>
          <span className="ql-verified" data-testid="hero-verified-badge">
            <ShieldCheck size={14} strokeWidth={1.4} aria-hidden />
            Verified in India · Direct from factory
          </span>
        </div>
        <div className="ql-hero-meta">
          {HERO_META.map((m) => (
            <div key={m.k} className="ql-hero-meta-tile">
              <span className="k">{m.k}</span>
              <span className="v">{m.v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/** 3-tile capabilities showcase — Furniture · Lighting · Home décor. */
const CapabilitiesSection = () => (
  <section className="ql-caps" data-testid="capabilities-section">
    <div className="ql-caps-head">
      <div>
        <p className="ql-caps-eyebrow">Capabilities</p>
        <h2 className="ql-caps-h2">Three core disciplines. Fifteen manufacturing clusters.</h2>
      </div>
      <p className="ql-caps-sub">
        Deep vertical expertise in the categories that dominate premium retail — engineered for
        small-batch craft economics and mass-market reliability alike.
      </p>
    </div>
    <div className="ql-caps-grid">
      {CAPABILITIES.map((c) => (
        <Link
          key={c.id}
          to={c.to}
          className="ql-cap-tile"
          data-testid={`capability-${c.id}`}
        >
          <img src={c.image} alt={`${c.label} sourcing — Asia Sourcing`} className="ql-cap-tile-img" loading="lazy" />
          <div className="ql-cap-tile-veil" aria-hidden />
          <div className="ql-cap-tile-body">
            <span className="ql-cap-ix">/ {c.ix} · {c.tagline}</span>
            <h3 className="ql-cap-h3">{c.label}</h3>
            <p className="ql-cap-p">{c.body}</p>
            <span className="ql-cap-cta">
              Explore {c.label.toLowerCase()} <ArrowRight size={13} strokeWidth={1.6} aria-hidden />
            </span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const STRENGTHS = [
  { ix: "01", title: "Unmatched integrity", body: "Complete transparency on costs, lead times and inspection — documented at every checkpoint." },
  { ix: "02", title: "India-wide network", body: "Fifteen manufacturing clusters across India, eight with permanent on-ground team presence — Moradabad, Jaipur, Jodhpur, Firozabad, Saharanpur, Panipat, Noida, Delhi." },
  { ix: "03", title: "1,000+ vetted factories", body: "Personally walked, capacity and compliance verified. Three to five shortlisted per brief." },
  { ix: "04", title: "Leading retailers &amp; brands", body: "Twenty-three years serving European and North-American retail, hospitality and independent labels." },
  { ix: "05", title: "Tailor-made product development", body: "Drawings, references and ideas turned into makeable products in costed samples within six weeks." },
  { ix: "06", title: "Quality &amp; social compliance", body: "AQL 2.5 standard, BSCI · Sedex · ISO 9001 · FSC · GOTS — photo-documented inspection on every order." },
  { ix: "07", title: "Speed to market", body: "Consolidation hub in Delhi NCR, six active export ports — one bill of lading, one ship date." },
  { ix: "08", title: "Documented supply chain", body: "Every order traceable from drawing to dock. Container loading photographed, certificates archived." },
];

const PRODUCTS = [
  {
    cat: "Lighting",
    slug: "lighting",
    title: "Brass · glass · ceramic",
    image:
      "https://customer-assets.emergentagent.com/job_india-sourcing-hub-2/artifacts/198wyawf_IMG_0955.jpeg",
  },
  {
    cat: "Furniture",
    slug: "furniture",
    title: "Solid wood · iron · upholstery",
    image:
      "https://customer-assets.emergentagent.com/job_india-sourcing-hub-2/artifacts/20e6rqo4_F50559EF-7C15-4181-B116-EF999534EB5A.png",
  },
  {
    cat: "Home décor",
    slug: "home-decor",
    title: "Ceramic · blue pottery · mixed media",
    image:
      "https://customer-assets.emergentagent.com/job_india-sourcing-hub-2/artifacts/4vshlb6w_2488C0C8-9F5F-49CB-B89B-957A4819DF61.png",
  },
  {
    cat: "Textile",
    slug: "textile",
    title: "Block print · weave · soft furnishing",
    image:
      "https://customer-assets.emergentagent.com/job_india-sourcing-hub-2/artifacts/ke1tjwv7_751CB0D0-3519-43CA-9552-86751A45263F.png",
  },
  {
    cat: "Brass & metalware",
    slug: "brass-metalware",
    title: "Casting · spinning · electroplating",
    image:
      "https://customer-assets.emergentagent.com/job_india-sourcing-hub-2/artifacts/hz98lfmc_B85AC5D2-0E01-448A-BFEE-C4E8E687E4D3.png",
  },
  {
    cat: "Fashion accessories",
    slug: "fashion-accessories",
    title: "Leather · jewellery · embroidered apparel",
    image:
      "https://customer-assets.emergentagent.com/job_india-sourcing-hub-2/artifacts/7g6k693j_533604A4-F3C4-4595-8192-0C84FBD22D72.png",
  },
];

export const Home = () => {
  useSeo({
    title: "India Sourcing Agent | Lighting, Furniture & Home Décor | Asia Sourcing",
    description:
      "India sourcing agent for lighting, furniture & home décor. 23 yrs · 15 clusters · 1,000+ vetted factories. Direct-from-factory, QC-controlled, from Moradabad.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [SERVICES_LD, TESTING_LD, FAQ_LD],
    },
    jsonLdId: "home-jsonld",
  });

  return (
    <div data-testid="home-page">
      {/* Cinematic brand intro — rendered at page root so it layers above
          the fixed header (hero has isolation:isolate which would trap it) */}
      <LogoReveal />

      {/* PREMIUM HERO — quiet luxury / industrial */}
      <QuietLuxuryHero />

      {/* CAPABILITIES SHOWCASE */}
      <CapabilitiesSection />

      {/* THE ASIA SOURCING DIFFERENCE */}
      <section className="difference" id="difference" data-testid="difference-section">
        <div className="wrap inner">
          <Reveal>
            <p className="eyebrow">The Asia Sourcing difference</p>
            <h2 className="display">We don't just source products. We source certainty.</h2>
          </Reveal>
          <Reveal>
            <p>
              Asia Sourcing is a Moradabad-based sourcing &amp; inspection control company for
              international buyers. We manage the India supply chain requirements of retailers and
              brands across lighting, furniture and home décor — combining 23 years of factory-floor
              experience with documented quality assurance and social compliance.
            </p>
            <p>
              Unlike a commission agent or a directory, we treat each client's business as our own.
              Our global leverage and permanent on-the-ground presence in eight of fifteen clusters give our clients the
              best product at the best possible price — documented end to end.
            </p>
            <div className="row">
              <Link to="/about" className="btn btn-fill" data-testid="difference-cta-about">
                See how we work →
              </Link>
              <Link to="/capabilities" className="btn btn-ghost">Explore services</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FROM STRENGTH TO STRENGTH */}
      <section className="strength" id="strength" data-testid="strength-section">
        <div className="wrap">
          <div className="head">
            <Reveal>
              <p className="eyebrow eyebrow-light">From strength to strength</p>
              <h2>What you get when you work with us.</h2>
            </Reveal>
            <Reveal>
              <p className="mono" style={{ color: "rgba(239,234,224,.5)" }}>
                Eight pillars · One documented chain
              </p>
            </Reveal>
          </div>
          <div className="strength-grid">
            {STRENGTHS.map((s) => (
              <Reveal
                key={s.ix}
                className="strength-tile"
                data-testid={`strength-tile-${s.ix}`}
              >
                <span className="ix">/ {s.ix}</span>
                <h3 dangerouslySetInnerHTML={{ __html: s.title }} />
                <p>{s.body}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="cta-row">
            <Link to="/capabilities" className="btn btn-fill on-dark btn-glow" data-testid="strength-cta">
              <span>Explore our services →</span>
            </Link>
            <Link to="/clusters" className="btn btn-ghost on-dark">See the clusters</Link>
          </Reveal>
        </div>
      </section>

      {/* TESTING & INSPECTION CATEGORIES */}
      <section className="testing" id="testing" data-testid="testing-section">
        <div className="wrap">
          <div className="head">
            <Reveal as="p" className="eyebrow">Testing &amp; inspection categories</Reveal>
            <Reveal as="h2">Every category we audit, inspect and certify.</Reveal>
            <Reveal as="p" className="lede" delay={150}>
              From sample sign-off to container seal — fourteen documented inspection categories,
              run to ISO and AQL standards, photographed and archived on every order.
            </Reveal>
          </div>
          <div className="testing-grid">
            {TESTING_CATEGORIES.map((t, i) => (
              <Reveal
                key={t.id}
                className="test-tile"
                data-testid={`testing-tile-${t.id}`}
              >
                <span className="ix">/ {String(i + 1).padStart(2, "0")}</span>
                <h3>{t.name}</h3>
                <p>{t.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER QUOTE */}
      <section className="wrap quote-block" data-testid="founder-quote">
        <Reveal className="quote-portrait">
          <img
            src="/assets/team/dr-sumit-chhabra.png"
            alt="Dr. Sumit Chhabra — founder, Asia Sourcing"
            width="480"
            height="640"
            loading="lazy"
          />
        </Reveal>
        <Reveal>
          <figure>
            <blockquote>
              "We will represent one interest, and one interest only — that of our client.
              And we will represent it to the best of our abilities."
            </blockquote>
            <figcaption>
              <strong>Dr. Sumit Chhabra</strong>
              Founder &amp; CEO · Moradabad · 2002
            </figcaption>
            <Link to="/about" className="btn btn-ghost" style={{ paddingLeft: 0, marginTop: "1.4rem" }} data-testid="quote-cta">
              Read the studio →
            </Link>
          </figure>
        </Reveal>
      </section>

      {/* PRODUCTS SOURCED */}
      <section className="products wrap" id="products" data-testid="products-section">
        <div className="head">
          <Reveal as="p" className="eyebrow">Products sourced</Reveal>
          <Reveal as="h2" className="display">
            Developed, sourced and delivered across six categories.
          </Reveal>
          <Reveal as="p" className="lede" delay={150}>
            Lighting, furniture, home décor, textile, brass metalware and fashion accessories — six categories, eight clusters,
            one documented chain.
          </Reveal>
        </div>
        <div className="products-grid">
          {PRODUCTS.map((p) => (
            <Reveal
              key={p.cat}
              data-testid={`product-card-${p.cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <Link to={`/categories/${p.slug}`} className="product-card">
                <img src={p.image} alt={`${p.cat} — Asia Sourcing`} width="800" height="600" loading="lazy" />
                <div className="label">
                  <span className="cat">{p.cat}</span>
                  <h3>{p.title}</h3>
                  <span className="arrow">EXPLORE →</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLUSTER MAP / GLOBAL NETWORK */}
      <section className="clusters" id="clusters">
        <div className="wrap grid">
          <Reveal>
            <p className="eyebrow eyebrow-light">India network</p>
            <h2>Fifteen clusters. Six categories. Eight with our team on the ground.</h2>
            <p className="lede">
              Our manufacturing-cluster presence across India provides unparalleled access to
              vetted factories. Managing the flow of goods from the factory floor to your
              container is our core competency.
            </p>
            <Link to="/clusters" className="btn btn-ghost on-dark" data-testid="clusters-link">
              Join our network →
            </Link>
          </Reveal>
          <Reveal>
            <ClusterMap />
          </Reveal>
        </div>
      </section>

      {/* SELECTED WORK */}
      <section className="work wrap" id="work">
        <div className="work-head">
          <div>
            <Reveal as="p" className="eyebrow">Selected work</Reveal>
            <Reveal as="h2">Documented from drawing to dock.</Reveal>
          </div>
          <Reveal>
            <Link to="/work" className="btn btn-ghost" data-testid="work-all-link">
              All case studies →
            </Link>
          </Reveal>
        </div>
        <div className="rail">
          {cases.map((c) => (
            <Reveal key={c.slug}>
              <Link to={`/work/${c.slug}`} className="card tilt" data-testid={`work-card-${c.slug}`}>
                <div className="plate">
                  <img src={c.image} alt={`${c.title} — case study`} width="800" height="600" loading="lazy" />
                  <span className="tag">{c.tag}</span>
                </div>
                <h3>{c.title}</h3>
                <p className="spec">{c.spec}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROOF */}
      <section className="proof wrap">
        <div className="proof-grid">
          <Reveal className="stat"><div className="num"><CountUp target={1000} suffix="+" testId="stat-factories" /></div><div className="lbl">Factories visited</div></Reveal>
          <Reveal className="stat"><div className="num"><CountUp target={6} testId="stat-ports" /></div><div className="lbl">Export ports</div></Reveal>
          <Reveal className="stat"><div className="num"><CountUp target={99} suffix="%" testId="stat-qc" /></div><div className="lbl">QC pass rate</div></Reveal>
          <Reveal className="stat"><div className="num"><CountUp target={23} suffix="+" testId="stat-years" /></div><div className="lbl">Years in India</div></Reveal>
        </div>
      </section>

      {/* FAQ — AI-search friendly Q&A */}
      <section className="faq wrap" id="faq" data-testid="faq-section">
        <Reveal as="p" className="eyebrow">Frequently asked</Reveal>
        <Reveal as="h2">Buyer questions, answered straight.</Reveal>
        <ul className="faq-list">
          {FAQ_LD.mainEntity.map((q, i) => (
            <Reveal as="li" key={i} className="faq-item">
              <details>
                <summary data-testid={`faq-q-${i}`}>{q.name}</summary>
                <p data-testid={`faq-a-${i}`}>{q.acceptedAnswer.text}</p>
              </details>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="cta wrap" id="enquire">
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">Tell us the category. We'll find the cluster.</Reveal>
        <Reveal className="row">
          <Link to="/contact" className="btn btn-fill btn-lg btn-glow" data-testid="cta-enquiry">
            <span>Send an enquiry →</span>
          </Link>
          <Link to="/capabilities" className="btn btn-ghost" data-testid="cta-process">
            See how we work
          </Link>
        </Reveal>
      </section>

      {/* Sprint 17 · Latest Insights — additive module above footer */}
      <LatestInsights />
    </div>
  );
};

export default Home;
